export type AccountingSection = "documents" | "payments" | "invoice_reconciliation" | "purchase_orders" | "purchase_order_reconciliation";

export type CommercialFilter =
  | "attention"
  | "overdue"
  | "paid"
  | "review"
  | "open"
  | "approved"
  | "history"
  | "lost";

export type WorkspaceView =
  | { type: "search" }
  | { type: "agent" }
  | { type: "booking_agent" }
  | { type: "learning" }
  | { type: "memberships" }
  | { type: "texts" }
  | { type: "communications" }
  | { type: "projects"; projectId?: string }
  | { type: "customers"; search: string }
  | { type: "customer"; customerId: string; returnTo: "customers" }
  | { type: "location"; customerId: string; propertyId: string }
  | { type: "dispatch"; date: string }
  | { type: "timesheets" }
  | { type: "job"; jobId: string; returnTo: "dispatch" | "customer" | "commercial"; customerId?: string; date: string }
  | { type: "invoice"; invoiceId: string; collectPayment?: boolean; jobId?: string; returnTo: "customer" | "job" | "commercial" | "memberships" | "field"; customerId?: string; date?: string; fieldTechnicianId?: string }
  | { type: "estimate"; estimateId: string; jobId?: string; returnTo: "customer" | "job" | "commercial" | "dispatch"; customerId?: string; date?: string }
  | { type: "pricebook" }
  | { type: "inventory"; forJobId?: string; forJobNumber?: string; purchaseOrderId?: string }
  | { type: "foundry_analysis" }
  | { type: "commercial"; mode: "invoices" | "estimates"; filter: CommercialFilter; section?: AccountingSection };

export type WorkspaceNavId = "projects" | "search" | "agent" | "learning" | "memberships" | "texts" | "communications" | "dispatch" | "timesheets" | "customers" | "pricebook" | "inventory" | "commercial" | "foundry_analysis";
export type TopLevelWorkspaceView = Extract<WorkspaceView, { type: WorkspaceNavId }>;

export const workspaceNav: Array<{ id: WorkspaceNavId; label: string }> = [
  { id: "agent", label: "Agent" },
  { id: "learning", label: "Learning" },
  { id: "dispatch", label: "Dispatch" },
  { id: "texts", label: "Texts" },
  { id: "communications", label: "Communications" },
  { id: "timesheets", label: "Timesheets" },
  { id: "customers", label: "Customers" },
  { id: "projects", label: "Projects" },
  { id: "memberships", label: "Memberships" },
  { id: "pricebook", label: "Pricebook" },
  { id: "inventory", label: "Inventory" },
  { id: "commercial", label: "Accounting" },
  { id: "foundry_analysis", label: "Palantir Foundry Analysis" },
];

export function isWorkspaceNavActive(view: WorkspaceView, id: WorkspaceNavId) {
  const customerRecord = (view.type === "job" && view.returnTo === "customer")
    || ((view.type === "invoice" || view.type === "estimate") && Boolean(view.customerId) && (view.returnTo === "customer" || view.returnTo === "job"));
  return id === view.type
    || (id === "communications" && view.type === "booking_agent")
    || (id === "customers" && (view.type === "customer" || view.type === "location" || customerRecord))
    || (id === "dispatch" && ((view.type === "job" && !customerRecord) || (view.type === "estimate" && view.returnTo === "dispatch")))
    || (id === "commercial" && !customerRecord && (view.type === "invoice" || (view.type === "estimate" && view.returnTo !== "dispatch")));
}

export function topLevelWorkspaceView(
  id: WorkspaceNavId,
  context: { search: string; date: string; mode: "invoices" | "estimates"; filter: CommercialFilter },
): TopLevelWorkspaceView {
  if (id === "projects") return { type: "projects" };
  if (id === "search") return { type: "search" };
  if (id === "agent") return { type: "agent" };
  if (id === "memberships") return { type: "memberships" };
  if (id === "learning") return { type: "learning" };
  if (id === "texts") return { type: "texts" };
  if (id === "communications") return { type: "communications" };
  if (id === "customers") return { type: "customers", search: context.search };
  if (id === "dispatch") return { type: "dispatch", date: context.date };
  if (id === "timesheets") return { type: "timesheets" };
  if (id === "pricebook") return { type: "pricebook" };
  if (id === "inventory") return { type: "inventory" };
  if (id === "foundry_analysis") return { type: "foundry_analysis" };
  return { type: "commercial", mode: context.mode, filter: context.filter };
}

export function parentWorkspaceView(
  view: Extract<WorkspaceView, { type: "customer" | "job" }>,
  context: { search: string },
): TopLevelWorkspaceView {
  if (view.type === "customer") return { type: "customers", search: context.search };
  return { type: "dispatch", date: view.date };
}

export type WorkspaceLandingAction =
  | "apply-url"
  | "apply-role-default"
  | "keep-current";

/**
 * The chosen workspace screen lives in React state. Only a handful of query
 * strings are deep links. The role default (Agent for owners, Dispatch for
 * dispatchers) is the first landing, not a view to restore whenever auth or
 * access is read again.
 */
export function workspaceLandingAction(input: {
  search: string;
  appliedSearch: string | null;
  hasLinkedView: boolean;
}): WorkspaceLandingAction {
  if (input.appliedSearch === input.search) return "keep-current";
  if (input.hasLinkedView) return "apply-url";
  if (input.appliedSearch === null) return "apply-role-default";
  return "keep-current";
}
